import { API_URL, authenticatedFetch } from "../config/api";

export interface SowParseRequest {
  text: string;
  project_hint?: string;
}

/**
 * Parse raw scope-of-work text into a draft project plus review-only
 * estimation metadata.
 *
 * Always requests `include_estimation=true` so the response is the
 * `{ project, estimation }` wrapper (SOWParseResponseSchema).
 *
 * The backend rejects non-SOW / low-signal input with a 400 (SOWParserError);
 * those are surfaced as friendly, retryable messages.
 */
export const parseSow = async (payload: SowParseRequest): Promise<SOWParseResult> => {
  const r = await authenticatedFetch(`${API_URL}/ai/sow/parse?include_estimation=true`, {
    method: 'POST',
    body: JSON.stringify({
      text: payload.text,
      project_hint: payload.project_hint,
      include_estimation: true
    })
  });

  if (!r.ok) {
    throw new Error(await parseSowErrorMessage(r));
  }

  const data = await r.json();

  // With include_estimation the response is the wrapper; be tolerant of a bare
  // project (legacy / include_estimation ignored) so the UI never crashes.
  if (data && typeof data === 'object' && 'project' in data) {
    return {
      project: normalizeParsedProject(data.project),
      estimation: normalizeEstimation(data.estimation)
    };
  }

  return {
    project: normalizeParsedProject(data),
    estimation: normalizeEstimation(null)
  };
};

/** Friendly message for a SOW parser rejection or failure. */
const parseSowErrorMessage = async (r: Response): Promise<string> => {
  let detail = '';
  try {
    const body = await r.json();
    if (typeof body?.detail === 'string') detail = body.detail;
    else if (Array.isArray(body?.detail) && body.detail[0]?.msg) detail = body.detail[0].msg;
  } catch {
    // non-JSON body
  }

  if (r.status === 400) {
    // The parser raises for empty / low-signal / non-SOW input. It sometimes
    // answers with a bare status phrase, which tells the user nothing about
    // what to change, so the guidance wins over an uninformative detail.
    return isInformative(detail)
      ? detail
      : "That doesn't look like a scope of work. Add more detail about the tasks, materials, or site and try again.";
  }
  return detail || `Failed to parse the scope of work (${r.status})`;
};

/** Generic status phrases carry no more information than the status code. */
const UNINFORMATIVE_DETAILS = ['bad request', 'unprocessable entity', 'internal server error', 'error'];

const isInformative = (detail: string): boolean =>
  detail.trim().length > 0 && !UNINFORMATIVE_DETAILS.includes(detail.trim().toLowerCase());

const normalizeParsedProject = (raw: unknown): SOWParsedProject => {
  const p = (raw ?? {}) as Record<string, unknown>;
  return {
    name: typeof p.name === 'string' && p.name ? p.name : 'Untitled Scope',
    description: typeof p.description === 'string' ? p.description : '',
    status: typeof p.status === 'string' ? p.status : 'NOT_STARTED',
    budget: typeof p.budget === 'number' ? p.budget : null,
    phases: Array.isArray(p.phases) ? (p.phases as SOWParsedPhase[]) : [],
    items: Array.isArray(p.items) ? (p.items as SOWParsedTask[]) : [],
    action_items: Array.isArray(p.action_items) ? (p.action_items as SOWParsedTask[]) : []
  };
};

const normalizeEstimation = (raw: unknown): SOWEstimation => {
  const e = (raw ?? {}) as Record<string, unknown>;
  return {
    estimated_people: typeof e.estimated_people === 'number' ? e.estimated_people : null,
    // The API types total_cost as number | string; keep it a string so the
    // review form can edit it freely.
    total_cost:
      typeof e.total_cost === 'string' ? e.total_cost
      : typeof e.total_cost === 'number' ? String(e.total_cost)
      : null,
    currency: typeof e.currency === 'string' ? e.currency : null,
    confidence: typeof e.confidence === 'number' ? e.confidence : null,
    assumptions: Array.isArray(e.assumptions) ? e.assumptions.filter((a): a is string => typeof a === 'string') : []
  };
};

// ---- Approve: apply an edited draft to an existing project ----------------

const DEFAULT_STATUS = 'NOT_STARTED';

/**
 * Top-level project fields echoed back on apply.
 *
 * `SOWParseResponseSchema` reuses `ProjectCreateSchema`, so the apply body has
 * to carry the whole project. The parser fills those fields from the pasted
 * text — it invents a project name and always returns `budget: 0` — and the
 * schema defaults `budget` to `0.0` when omitted. Either would quietly
 * overwrite live project data, so every scalar is resent from the project as
 * it stands and only the nested phases / items / action items come from the
 * draft.
 */
const ECHOED_PROJECT_FIELDS = [
  'name',
  'description',
  'sales_order_number',
  'start_date',
  'end_date',
  'actual_end_date',
  'status',
  'budget',
  'owner_id',
  'client_id',
  'timezone'
] as const;

/** RFC4122 id for phases the user adds; parsed phases keep the id they arrived with. */
export const newPhaseId = (): string => {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // happy-dom / older browsers.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

/** Thrown when the apply endpoint rejects the payload; `details` lists every message. */
export class SowApplyError extends Error {
  details: string[];
  constructor(details: string[]) {
    super(details[0] || 'Failed to apply the scope of work');
    this.name = 'SowApplyError';
    this.details = details;
  }
}

/**
 * Build the `POST /projects/{id}/sow/apply` body from the reviewed draft.
 *
 * Phases carry their id and children point at it through `phase_id`, which is
 * the same convention the parser returns — so the backend resolves the links
 * itself and no phase has to be created up front. Every nested entity is
 * stamped with the real project id; the ones the parser returns carry an
 * ephemeral id for a project that does not exist.
 */
export const buildApplyPayload = (
  projectId: string,
  draft: SOWDraft,
  projectData: ProjectInterface
): SOWApplyRequest => {
  const source = (projectData ?? {}) as unknown as Record<string, unknown>;
  const project: Record<string, unknown> = {};
  for (const field of ECHOED_PROJECT_FIELDS) {
    if (source[field] !== undefined) project[field] = source[field];
  }

  return {
    project: {
      ...project,
      // Both are required by the schema, so they always need a value.
      name: typeof source.name === 'string' && source.name ? source.name : draft.projectName,
      status: typeof source.status === 'string' && source.status ? source.status : DEFAULT_STATUS,
      phases: draft.phases.map(phase => ({
        id: phase.key,
        project_id: projectId,
        name: phase.name,
        description: phase.description || null,
        status: DEFAULT_STATUS
      })),
      items: draft.items.map(item => ({
        id: null as null,
        project_id: projectId,
        phase_id: item.phaseKey,
        name: item.name,
        description: item.description || null,
        status: DEFAULT_STATUS,
        priority: item.priority
      })),
      action_items: draft.actionItems.map(action => ({
        id: null as null,
        project_id: projectId,
        phase_id: action.phaseKey,
        name: action.name,
        description: action.description || null,
        status: DEFAULT_STATUS
      }))
    },
    estimation: {
      estimated_people: parseCount(draft.estimation.estimatedPeople),
      total_cost: draft.estimation.totalCost.trim() || null,
      currency: draft.estimation.currency.trim() || null,
      confidence: draft.estimation.confidence,
      assumptions: draft.estimation.assumptions.map(a => a.trim()).filter(Boolean)
    }
  };
};

/**
 * Apply an approved SOW draft to an existing project.
 *
 * One call: the backend validates the top-level fields, applies the nested
 * phases / items / action items, copies the approved total cost into the
 * project budget and answers 204.
 *
 * @throws SowApplyError with one resolved message per validation error, so the
 *   caller can show them all and keep the draft for correction.
 */
export const approveSowDraft = async (
  projectId: string,
  draft: SOWDraft,
  projectData: ProjectInterface
): Promise<SOWApproveReport> => {
  const r = await authenticatedFetch(`${API_URL}/projects/${projectId}/sow/apply`, {
    method: 'POST',
    body: JSON.stringify(buildApplyPayload(projectId, draft, projectData))
  });

  if (!r.ok) {
    throw new SowApplyError(await applyErrorMessages(r, draft));
  }

  return {
    phases: draft.phases.length,
    items: draft.items.length,
    actionItems: draft.actionItems.length
  };
};

/**
 * Turn a rejection into one message per problem.
 *
 * FastAPI reports each validation error with a `loc` path into the body — e.g.
 * `["body", "project", "phases", 0, "name"]`. The payload nests every phase and
 * task, so a bare message would leave the user hunting; each one is resolved
 * back to the row it came from.
 */
const applyErrorMessages = async (r: Response, draft: SOWDraft): Promise<string[]> => {
  let body: unknown;
  try {
    body = await r.json();
  } catch {
    return [`Failed to apply the scope of work (${r.status})`];
  }

  const detail = (body as { detail?: unknown })?.detail;
  if (typeof detail === 'string') return [detail];

  if (Array.isArray(detail)) {
    const messages = detail
      .map(entry => {
        const msg = typeof entry?.msg === 'string' ? entry.msg : '';
        if (!msg) return '';
        const where = describeLoc(Array.isArray(entry?.loc) ? entry.loc : [], draft);
        return where ? `${where}: ${msg}` : msg;
      })
      .filter(Boolean);
    if (messages.length) return messages;
  }

  return [`Failed to apply the scope of work (${r.status})`];
};

/** Human-readable name for the row a `loc` path points at. */
const describeLoc = (loc: (string | number)[], draft: SOWDraft): string => {
  const label = (kind: string, index: number, name: string | undefined, field: string | number | undefined) => {
    const title = name?.trim() ? `"${name.trim()}"` : `#${index + 1}`;
    return field ? `${kind} ${title} → ${field}` : `${kind} ${title}`;
  };

  for (let i = 0; i < loc.length; i++) {
    const segment = loc[i];
    const index = loc[i + 1];
    const field = loc[i + 2];
    if (typeof index !== 'number') continue;

    if (segment === 'phases') return label('Phase', index, draft.phases[index]?.name, field);
    if (segment === 'items') return label('Task', index, draft.items[index]?.name, field);
    if (segment === 'action_items') return label('Action item', index, draft.actionItems[index]?.name, field);
  }

  if (loc.includes('estimation')) return `Estimate → ${loc[loc.length - 1]}`;
  if (loc.includes('project')) return `Project → ${loc[loc.length - 1]}`;
  return '';
};

/** Parse an edited head-count into a non-negative integer; blank/invalid -> null. */
export const parseCount = (value: string | null | undefined): number | null => {
  const n = parseCost(value);
  if (n === null || n < 0) return null;
  return Math.round(n);
};

/** Parse a numeric-string cost into a number; null/blank/invalid -> null. */
export const parseCost = (value: string | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
};

/** Build the initial editable draft from a raw parse result. */
export const draftFromParseResult = (result: SOWParseResult): SOWDraft => {
  // The phase key doubles as the id sent back on apply, so it has to be a real
  // uuid: keep the one the parser assigned, and mint one for a phase that
  // arrived without it. Tasks go on referencing their phase by key.
  const phaseIdToKey = new Map<string, string>();
  const phases: SOWDraftPhase[] = result.project.phases.map(p => {
    const key = p.id || newPhaseId();
    if (p.id) phaseIdToKey.set(p.id, key);
    return { key, name: p.name || '', description: p.description || '' };
  });

  const phaseKeyFor = (phaseId?: string | null): string | null =>
    (phaseId && phaseIdToKey.get(phaseId)) || null;

  const items: SOWDraftItem[] = result.project.items.map((it, i) => ({
    key: `item-${i}`,
    phaseKey: phaseKeyFor(it.phase_id),
    name: it.name || '',
    description: it.description || '',
    priority: normalizePriority(it.priority)
  }));

  const actionItems: SOWDraftActionItem[] = result.project.action_items.map((ai, i) => ({
    key: `action-${i}`,
    phaseKey: phaseKeyFor(ai.phase_id),
    name: ai.name || '',
    description: ai.description || ''
  }));

  const est = result.estimation;
  return {
    projectName: result.project.name || '',
    phases,
    items,
    actionItems,
    estimation: {
      estimatedPeople: est.estimated_people != null ? String(est.estimated_people) : '',
      // total_cost is number | string on the wire; the form edits it as text.
      totalCost: est.total_cost != null ? String(est.total_cost) : '',
      currency: est.currency ?? '',
      confidence: est.confidence ?? null,
      assumptions: est.assumptions ?? []
    }
  };
};

const normalizePriority = (p?: string | null): 'LOW' | 'MEDIUM' | 'HIGH' => {
  const upper = (p || '').toUpperCase();
  return upper === 'LOW' || upper === 'HIGH' ? upper : 'MEDIUM';
};
